import { Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1A1A2E] text-white py-12">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center text-white/70 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-white/70">Last updated: December 2024</p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-12 prose prose-slate max-w-none">
          
          <h2>1. Introduction</h2>
          <p>
            1031Condo.com ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services. This policy complies with the General Data Protection Regulation (GDPR), the California Consumer Privacy Act (CCPA), and other applicable privacy laws.
          </p>

          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Personal Information You Provide</h3>
          <ul>
            <li><strong>Contact Information:</strong> Name, email address, phone number, mailing address</li>
            <li><strong>Account Information:</strong> Login credentials when you create an account</li>
            <li><strong>Financial Information:</strong> Information about your 1031 exchange requirements, property preferences, and investment criteria</li>
            <li><strong>Communications:</strong> Records of correspondence when you contact us</li>
          </ul>

          <h3>2.2 Information Automatically Collected</h3>
          <ul>
            <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
            <li><strong>Usage Data:</strong> Pages visited, time spent on pages, click patterns</li>
            <li><strong>Cookies:</strong> Session cookies, preference cookies, analytics cookies</li>
          </ul>

          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide and maintain our services</li>
            <li>Process your inquiries and requests</li>
            <li>Send you property listings and market updates (with your consent)</li>
            <li>Improve our website and services</li>
            <li>Comply with legal obligations</li>
            <li>Protect against fraudulent or unauthorized activity</li>
          </ul>

          <h2>4. Legal Basis for Processing (GDPR)</h2>
          <p>Under the GDPR, we process your personal data based on:</p>
          <ul>
            <li><strong>Consent:</strong> When you opt-in to receive marketing communications</li>
            <li><strong>Contract:</strong> When necessary to provide services you've requested</li>
            <li><strong>Legitimate Interests:</strong> For business operations and improving services</li>
            <li><strong>Legal Obligation:</strong> When required by law</li>
          </ul>

          <h2>5. Your Rights Under GDPR</h2>
          <p>If you are in the European Economic Area (EEA), you have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request copies of your personal data</li>
            <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
            <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
            <li><strong>Restrict Processing:</strong> Request limitation of processing</li>
            <li><strong>Data Portability:</strong> Request transfer of your data to another service</li>
            <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
          </ul>

          <h2>6. Your Rights Under CCPA</h2>
          <p>If you are a California resident, you have the right to:</p>
          <ul>
            <li><strong>Know:</strong> Request disclosure of personal information collected about you</li>
            <li><strong>Delete:</strong> Request deletion of your personal information</li>
            <li><strong>Opt-Out:</strong> Opt-out of the sale of your personal information (Note: We do not sell personal information)</li>
            <li><strong>Non-Discrimination:</strong> Not be discriminated against for exercising your rights</li>
          </ul>
          <p>
            To exercise these rights, <a href="/contact">contact us here</a>.
          </p>

          <h2>7. Data Sharing and Disclosure</h2>
          <p>We may share your information with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Third parties who help us operate our business</li>
            <li><strong>Real Estate Partners:</strong> Licensed real estate professionals (with your consent)</li>
            <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
            <li><strong>Business Transfers:</strong> In connection with a merger or acquisition</li>
          </ul>
          <p><strong>We do not sell your personal information to third parties.</strong></p>

          <h2>8. Data Retention</h2>
          <p>
            We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. Typically, we retain:
          </p>
          <ul>
            <li>Account information: For the duration of your account plus 7 years</li>
            <li>Transaction records: 7 years as required by law</li>
            <li>Marketing preferences: Until you opt-out</li>
          </ul>

          <h2>9. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your personal information, including encryption, secure servers, and access controls. However, no method of transmission over the Internet is 100% secure.
          </p>

          <h2>10. International Data Transfers</h2>
          <p>
            If you are located outside the United States, your information may be transferred to and processed in the United States. We ensure appropriate safeguards are in place for such transfers in compliance with GDPR requirements.
          </p>

          <h2>11. Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to enhance your experience. You can control cookies through your browser settings. For more information, see our Cookie Policy section.
          </p>
          <h3>Types of Cookies We Use:</h3>
          <ul>
            <li><strong>Essential Cookies:</strong> Required for website functionality</li>
            <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
            <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements (with consent)</li>
          </ul>

          <h2>12. Children's Privacy</h2>
          <p>
            Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children.
          </p>

          <h2>13. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically. We will notify you of material changes by posting the new policy on this page and updating the "Last updated" date.
          </p>

          <h2>14. Contact Us</h2>
          <p>For questions about this Privacy Policy or to exercise your rights, contact us:</p>
          <ul>
            <li>Contact: <a href="/contact">Send us a message</a></li>
            <li>Company: Krieger Real Estate LLC</li>
            <li>Website: 1031Condo.com</li>
          </ul>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800 mb-0">
              <strong>Data Protection Inquiries:</strong> For GDPR-related inquiries or to exercise your privacy rights, <a href="/contact">contact us here</a>.
            </p>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-sm text-gray-700 mb-0">
              <strong>Company Information:</strong> 1031Condo.com is owned and operated by Krieger Real Estate LLC, a licensed real estate broker in New York State (License #10491202190) and Florida (License #CQ1072150).
            </p>
          </div>
        </div>

        <div className="mt-8 flex gap-4">
          <Link to="/terms">
            <Button variant="outline">Terms of Service</Button>
          </Link>
          <Link to="/disclaimer">
            <Button variant="outline">Disclaimer</Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
